import { titleCase } from "@/lib/format";
import type { Entitlement, PortalNotification } from "./types";

export const MONO = "var(--md-font-mono)";
export const MIN_PASSWORD = 10;

/** Plain text from the server (ticket bodies, articles): line breaks kept, no markup. */
export const preText = {
  m: 0,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  lineHeight: 1.65,
} as const;

/** Ticket statuses that still need something to happen. */
export const OPEN_STATUSES: readonly string[] = [
  "requested", "approved", "in_progress", "shipped", "received",
];

export function isOpen(status: string): boolean {
  return OPEN_STATUSES.includes(status);
}

export function isAdminRole(role: string | null | undefined): boolean {
  return role === "admin" || role === "customer_admin";
}

export function roleLabel(role: string | null | undefined): string {
  return isAdminRole(role) ? "Administrator" : "User";
}

export function errorMessage(error: unknown, fallback = "That did not work"): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error) return error;
  return fallback;
}

export type Tone = "default" | "success" | "warning" | "error" | "info";

function expired(value: string | null | undefined): boolean {
  if (!value) return false;
  const at = new Date(value).getTime();
  return !Number.isNaN(at) && at <= Date.now();
}

/** Active and not past its end date. */
export function isCurrent(entitlement: Entitlement): boolean {
  return (entitlement.status ?? "active") === "active" && !expired(entitlement.valid_until);
}

export function validity(entitlement: Entitlement): { label: string; tone: Tone } {
  const { status, valid_until: until } = entitlement;
  if (status && status !== "active") {
    return { label: titleCase(status), tone: status === "revoked" ? "error" : "default" };
  }
  if (!until) return { label: "No end date", tone: "success" };
  if (expired(until)) return { label: "Expired", tone: "error" };
  const at = new Date(until).getTime();
  if (Number.isNaN(at)) return { label: "Active", tone: "success" };
  const days = Math.ceil((at - Date.now()) / 86_400_000);
  if (days <= 30) return { label: days === 1 ? "1 day left" : `${days} days left`, tone: "warning" };
  return { label: "Active", tone: "success" };
}

export function kindLabel(kind: string | null | undefined): string {
  switch (kind) {
    case "base":
      return "Base licence";
    case "module":
      return "Module";
    case "support":
      return "Support plan";
    default:
      return titleCase(kind);
  }
}

export function entitlementName(entitlement: Entitlement): string {
  return entitlement.module_code || entitlement.sku_code || kindLabel(entitlement.kind);
}

/** "JBSW Y3DP EHPK 3PXP": easier to read aloud and to type than one long run. */
export function groupSecret(secret: string): string {
  return secret.replace(/(.{4})(?=.)/g, "$1 ");
}

export function priorityTone(priority: string): Tone {
  if (priority === "critical") return "error";
  if (priority === "high") return "warning";
  if (priority === "medium") return "info";
  return "default";
}

export function ticketTone(status: string): Tone {
  if (status === "resolved") return "success";
  if (status === "requested") return "warning";
  if (status === "rejected") return "error";
  if (isOpen(status)) return "info";
  return "default";
}

/** Where a notification leads, if anywhere. */
export function notificationLink(notification: PortalNotification): string | null {
  const { target_type: targetType, target_id: targetId, type } = notification;
  if (targetType === "ticket" && targetId) return `/tickets/${encodeURIComponent(targetId)}`;
  if (targetType === "release") return "/downloads";
  if (type.startsWith("license.") || type.startsWith("heartbeat.")) return "/licence";
  return null;
}

export function count(value: number | null | undefined): string {
  return value == null ? "—" : String(value);
}
